import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input.tsx";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select.tsx";
import { Button } from "@/components/ui/button.tsx";
import  Calendar  from "@/components/DatePicker.jsx";
import {useParams} from "react-router-dom";
import useMutateData from "@/hooks/useMutateData.js";
import { getSkills } from "@/api/skills.jsx";
import { toast } from "sonner";

export default function CandidateGeneralEdit({ data, onSave, onCancel }) {

    const { slug } = useParams();
    const [skills, setSkills] = useState([]);

    // Initialize form data
    const initialFormData = {
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        knownAs: data.knownAs || "",
        gender: data.gender || "",
        nationality: data.nationality || "",
        dob: data.dob || "",
        phoneNumber: data.phoneNumber || "",
        email: data.email || "",
        niNumber: data.niNumber || "",
        payrollNumber: data.payrollNumber || "",
        payMethod: data.payMethod || "",
        payrollMethod: data.payrollMethod || "",
        status: data.status || "",
        contactDate: data.contactDate || "",
        mainSkillId: data.mainSkillId || "",
        startDate: data.startDate || "",
        address_attributes: {
            line1: data.address?.line1 || "",
            line2: data.address?.line2 || "",
            line3: data.address?.line3 || "",
            town: data.address?.town || "",
            county: data.address?.county || "",
            postcode: data.address?.postcode || "",
        },
        bank_account_attributes: {
            accountHolderName: data.bankAccount?.accountHolderName || "",
            sortCode: data.bankAccount?.sortCode || "",
            bankAccountNumber: data.bankAccount?.bankAccountNumber || "",
        },
    };

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const skillsData = await getSkills();
                setSkills(skillsData || []);
            } catch (error) {
                console.error("Failed to fetch skills:", error);
            }
        };
        fetchSkills();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const [isSaving, setIsSaving] = useState(false);
    const { mutate, loading } = useMutateData(); // Use the hook

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log("Submitting form data:", formData); // Debugging log

        try {
            const updatedCandidate = await mutate("PUT", `admin/candidates/${slug}`, { candidate: formData });

            console.log("Updated candidate data:", updatedCandidate); // Debugging log
            toast.success("Candidate updated successfully");
            onSave(updatedCandidate); // Update UI state
        } catch (error) {
            console.error("Failed to update candidate:", error);

            // Extract all error messages from the response
            const responseData = error?.response?.data;
            const allErrors = [];

            if (responseData?.form_errors && responseData.form_errors.length > 0) {
                allErrors.push(...responseData.form_errors);
            }
            if (responseData?.candidate_errors && responseData.candidate_errors.length > 0) {
                allErrors.push(...responseData.candidate_errors);
            }
            if (responseData?.address_errors && responseData.address_errors.length > 0) {
                allErrors.push(...responseData.address_errors);
            }
            if (responseData?.bank_account_errors && responseData.bank_account_errors.length > 0) {
                allErrors.push(...responseData.bank_account_errors);
            }

            // Fallback to other error formats
            if (allErrors.length === 0) {
                if (responseData?.error) {
                    allErrors.push(responseData.error);
                } else if (responseData?.errors) {
                    allErrors.push(...(Array.isArray(responseData.errors) ? responseData.errors : [responseData.errors]));
                } else if (error?.message) {
                    allErrors.push(error.message);
                } else {
                    allErrors.push("Failed to update candidate");
                }
            }

            // Display all errors
            const errorMessage = allErrors.join(", ");
            toast.error(errorMessage);
        }
    };


    return (
        <form onSubmit={handleSubmit} className="p-6 border rounded-lg bg-white shadow-sm space-y-6">
            <h3 className="text-xl font-bold">Edit General Information</h3>

            {/* Personal Information */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Personal Details</h4>
                <label className="block">First Name
                    <Input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Last Name
                    <Input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Known As
                    <Input type="text" name="knownAs" value={formData.knownAs} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Gender
                    <Input type="text" name="gender" value={formData.gender} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Nationality
                    <Input type="text" name="nationality" value={formData.nationality} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Date of Birth
                    <Calendar value={formData.dob} onChange={(date) => setFormData({ ...formData, dob: date })} />
                </label>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Contact Information</h4>
                <label className="block">Phone Number
                    <Input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Email Address
                    <Input type="email" name="email" value={formData.email} onChange={handleChange} className="mt-1" />
                </label>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Address</h4>
                <label className="block">Line 1
                    <Input type="text" name="line1" value={formData.address_attributes?.line1 || ""}
                           onChange={(e) => setFormData({ ...formData, address_attributes: { ...formData.address_attributes, line1: e.target.value } })} />
                </label>
                <label className="block">Line 2
                    <Input type="text" name="line2" value={formData.address_attributes?.line2 || ""}
                           onChange={(e) => setFormData({ ...formData, address_attributes: { ...formData.address_attributes, line2: e.target.value } })} />
                </label>
                <label className="block">Line 3
                    <Input type="text" name="line3" value={formData.address_attributes?.line3 || ""}
                           onChange={(e) => setFormData({ ...formData, address_attributes: { ...formData.address_attributes, line3: e.target.value } })} />
                </label>
                <label className="block">Town
                    <Input type="text" name="town" value={formData.address_attributes?.town || ""}
                           onChange={(e) => setFormData({ ...formData, address_attributes: { ...formData.address_attributes, town: e.target.value } })} />
                </label>
                <label className="block">County
                    <Input type="text" name="county" value={formData.address_attributes?.county || ""}
                           onChange={(e) => setFormData({ ...formData, address_attributes: { ...formData.address_attributes, county: e.target.value } })} />
                </label>
                <label className="block">Postcode
                    <Input type="text" name="postcode" value={formData.address_attributes?.postcode || ""}
                           onChange={(e) => setFormData({ ...formData, address_attributes: { ...formData.address_attributes, postcode: e.target.value } })} />
                </label>
            </div>


            {/* Payroll Information */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Payroll Details</h4>
                <label className="block">NI Number
                    <Input type="text" name="niNumber" value={formData.niNumber || ""} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Payroll Number
                    <Input type="text" name="payrollNumber" value={formData.payrollNumber || ""} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Pay Method
                    <Input type="text" name="payMethod" value={formData.payMethod || ""} onChange={handleChange} className="mt-1" />
                </label>
                <label className="block">Payroll Method
                    <Input type="text" name="payrollMethod" value={formData.payrollMethod || ""} onChange={handleChange} className="mt-1" />
                </label>
            </div>

            {/* Bank Account Information */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Bank Account Details</h4>
                <label className="block">Account Holder Name
                    <Input type="text" name="accountHolderName" value={formData.bank_account_attributes?.accountHolderName || ""}
                           onChange={(e) => setFormData({ ...formData, bank_account_attributes: { ...formData.bank_account_attributes, accountHolderName: e.target.value } })} className="mt-1" />
                </label>
                <label className="block">Sort Code
                    <Input type="text" name="sortCode" value={formData.bank_account_attributes?.sortCode || ""}
                           onChange={(e) => setFormData({ ...formData, bank_account_attributes: { ...formData.bank_account_attributes, sortCode: e.target.value } })} className="mt-1" />
                </label>
                <label className="block">Bank Account Number
                    <Input type="text" name="bankAccountNumber" value={formData.bank_account_attributes?.bankAccountNumber || ""}
                           onChange={(e) => setFormData({ ...formData, bank_account_attributes: { ...formData.bank_account_attributes, bankAccountNumber: e.target.value } })} className="mt-1" />
                </label>
            </div>

            {/* Status Information */}
            <div className="space-y-4">
                <h4 className="text-lg font-semibold">Status</h4>
                <label className="block">Status
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                </label>
                <label className="block">Contact Date
                    <Calendar value={formData.contactDate} onChange={(date) => setFormData({ ...formData, contactDate: date })} />
                </label>
                <label className="block">Job Title
                    <Select value={formData.mainSkillId} onValueChange={(value) => setFormData({ ...formData, mainSkillId: value })}>
                        <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select job title" />
                        </SelectTrigger>
                        <SelectContent>
                            {skills.map((skill) => (
                                <SelectItem key={skill.id} value={skill.id.toString()}>
                                    {skill.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </label>
                <label className="block">Start Date
                    <Calendar value={formData.startDate} onChange={(date) => setFormData({ ...formData, startDate: date })} />
                </label>
            </div>

            <div className="flex justify-end space-x-2">
                <Button type="submit" disabled={isSaving}>
                    {isSaving ? <span className="animate-spin">🔄</span> : "Save"}
                </Button>
                <Button type="button" onClick={onCancel} variant="outline" disabled={isSaving}>
                    Cancel
                </Button>
            </div>
        </form>
    );
}
